import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Siren, ChevronDown, ChevronUp, ShieldCheck } from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { fmtDate } from '../../../lib/scheduleFormat';

const TIER_STYLES = {
  watch: { cls: 'border-slate-300 bg-slate-100 text-slate-700', label: 'Watching' },
  warning: { cls: 'border-amber-300 bg-amber-50 text-amber-800', label: 'Warning sent' },
  critical: { cls: 'border-red-300 bg-red-50 text-red-800', label: 'Critical' },
};

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');

// The durable record behind the board: what went red, when it was chased, and
// whether it was fixed before the delivery date arrived.
export const IncidentLog = () => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await adminService.getCoverageIncidents());
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const incidents = expanded ? data.incidents : data.incidents.slice(0, 4);

  return (
    <Card data-testid="incident-log">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Siren className="h-5 w-5 text-red-600" /> Escalation log
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={data.open_count
              ? 'border-red-300 bg-red-50 text-red-800'
              : 'border-emerald-300 bg-emerald-50 text-emerald-800'}
              data-testid="incident-open-count">
              {data.open_count} open
            </Badge>
            {data.critical_count > 0 && (
              <Badge variant="outline" className="border-red-400 bg-red-100 text-red-900"
                data-testid="incident-critical-count">
                {data.critical_count} critical
              </Badge>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500">
          A date that goes red opens an incident. Further out than your warning window it is watched
          quietly; inside it you get one email; inside the critical window you are chased daily until
          it clears.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {data.incidents.length === 0 ? (
          <div className="flex items-center gap-2 py-4 text-sm text-gray-600"
            data-testid="incident-log-empty">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            Nothing has gone red yet.
          </div>
        ) : (
          <>
            {incidents.map((incident) => {
              const tier = TIER_STYLES[incident.tier] || TIER_STYLES.watch;
              return (
                <div
                  key={incident.id}
                  className={`rounded-md border p-3 ${incident.resolved ? 'bg-white' : 'bg-red-50/40'}`}
                  data-testid={`incident-${incident.delivery_date}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        {fmtDate(incident.delivery_date)}
                        <span className="ml-2 font-normal text-gray-500">
                          {incident.order_count} order{incident.order_count === 1 ? '' : 's'}
                        </span>
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {incident.reasons.map((reason, index) => (
                          <li key={index} className="text-xs text-gray-700">• {reason.message}</li>
                        ))}
                      </ul>
                      <p className="mt-1 text-xs text-gray-500">
                        Opened {when(incident.opened_at)}
                        {incident.escalation_count > 0
                          ? ` · chased ${incident.escalation_count}×, last ${when(incident.last_escalated_at)}`
                          : ' · not chased yet'}
                        {incident.resolved && incident.open_hours != null
                          ? ` · ${incident.resolved_level === 'expired' ? 'expired' : 'fixed'} after ${incident.open_hours}h`
                          : ''}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {incident.resolved ? (
                        <Badge variant="outline" className={incident.resolved_level === 'expired'
                          ? 'border-slate-300 bg-slate-100 text-slate-700'
                          : 'border-emerald-300 bg-emerald-50 text-emerald-800'}>
                          {incident.resolved_level === 'expired' ? 'Date passed' : 'Resolved'}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className={tier.cls}>{tier.label}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {data.incidents.length > 4 && (
              <Button
                variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}
                data-testid="incident-log-toggle"
              >
                {expanded
                  ? <><ChevronUp className="mr-1 h-4 w-4" /> Show fewer</>
                  : <><ChevronDown className="mr-1 h-4 w-4" /> Show all {data.incidents.length}</>}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default IncidentLog;
